/**
 * DictationHelper
 *
 * Web Speech API dictation for the three text inputs that support a microphone:
 * the main question box ('main'), the chat history search ('chatSearch') and the
 * nested chat search ('nestedSearch').
 *
 * `initSpeechRecognition` wires up the recognizer once (from ngOnInit); the
 * transcript is appended to whatever the user had already typed, which is
 * snapshotted into `baseInputValue` when dictation starts.
 *
 * Extracted verbatim from chatbotexpand.component.ts (`this.` -> `comp.`);
 * behavior is unchanged. No bare `this` existed in this cluster.
 */
export class DictationHelper {

  static initSpeechRecognition(comp: any): void {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      comp.isSpeechSupported = true;
      comp.speechRecognition = new SpeechRecognition();
      comp.speechRecognition.continuous = true;
      comp.speechRecognition.interimResults = true;
      comp.speechRecognition.lang = 'en-US';

      comp.speechRecognition.onstart = () => {
        comp.isDictating = true;
      };

      comp.speechRecognition.onresult = (event: any) => {
        let sessionTranscript = '';

        // Iterate over all results in the current session
        for (let i = 0; i < event.results.length; ++i) {
          let phrase = event.results[i][0].transcript;
          if (sessionTranscript && !sessionTranscript.endsWith(' ') && !phrase.startsWith(' ')) {
            sessionTranscript += ' ';
          }
          sessionTranscript += phrase;
        }

        if (sessionTranscript) {
          sessionTranscript = sessionTranscript.replace(/[\.\?]/g, ' ').replace(/\s+/g, ' ');

          let newValue = comp.baseInputValue.replace(/\?$/, '').trim();
          newValue = newValue ? newValue + ' ' + sessionTranscript.trim() : sessionTranscript.trim();

          if (comp.activeDictationTarget === 'main') {
            newValue = newValue + '?';
            comp.searchForm.patchValue({ query: newValue });
            setTimeout(() => {
              const inputEl = document.querySelector('input[formControlName="query"]') as HTMLInputElement;
              if (inputEl) inputEl.scrollLeft = inputEl.scrollWidth;
            }, 0);
          } else if (comp.activeDictationTarget === 'chatSearch') {
            comp.chatSearchText = newValue;
            setTimeout(() => {
              const inputEl = document.querySelector('.chat-search-input') as HTMLInputElement;
              if (inputEl) inputEl.scrollLeft = inputEl.scrollWidth;
            }, 0);
            // Fire search automatically only when a segment is finalized
            if (event.results[event.results.length - 1].isFinal) {
              comp.onChatSearch();
            }
          } else if (comp.activeDictationTarget === 'nestedSearch') {
            comp.chatNestedSearchText = newValue;
            setTimeout(() => {
              const inputEl = document.querySelector('.chat-nested-input') as HTMLInputElement;
              if (inputEl) inputEl.scrollLeft = inputEl.scrollWidth;
            }, 0);
            if (event.results[event.results.length - 1].isFinal) {
              comp.onChatSearch();
            }
          }
        }
      };

      comp.speechRecognition.onerror = (event: any) => {
        console.error('Speech recognition error', event.error);
        comp.isDictating = false;
        if (event.error === 'not-allowed') {
          comp.showToast('Microphone access denied. Please allow microphone permissions.', 'error');
        }
      };

      comp.speechRecognition.onend = () => {
        comp.isDictating = false;
        comp.activeDictationTarget = null;
      };
    } else {
      comp.isSpeechSupported = false;
      console.warn('Speech Recognition API is not supported in this browser.');
    }
  }

  static toggleDictation(comp: any, target: 'main' | 'chatSearch' | 'nestedSearch'): void {
    if (!comp.isSpeechSupported) return;

    if (comp.isDictating) {
      if (comp.activeDictationTarget === target) {
        comp.stopDictation();
        comp.activeDictationTarget = null;
      } else {
        comp.stopDictation();
        setTimeout(() => {
          comp.activeDictationTarget = target;
          DictationHelper.captureBaseValue(comp, target);
          DictationHelper.startDictation(comp);
        }, 100);
      }
    } else {
      comp.activeDictationTarget = target;
      DictationHelper.captureBaseValue(comp, target);
      DictationHelper.startDictation(comp);
    }
  }

  private static captureBaseValue(comp: any, target: 'main' | 'chatSearch' | 'nestedSearch'): void {
    if (target === 'main') {
      comp.baseInputValue = comp.searchForm.get('query')?.value || '';
    } else if (target === 'chatSearch') {
      comp.baseInputValue = comp.chatSearchText || '';
    } else if (target === 'nestedSearch') {
      comp.baseInputValue = comp.chatNestedSearchText || '';
    }
  }

  private static startDictation(comp: any): void {
    if (comp.speechRecognition && !comp.isDictating) {
      try {
        comp.speechRecognition.start();
      } catch (e) {
        console.error('Failed to start dictation:', e);
      }
    }
  }

  static stopDictation(comp: any): void {
    if (comp.speechRecognition && comp.isDictating) {
      try {
        comp.speechRecognition.stop();
      } catch (e) {
        console.error('Failed to stop dictation:', e);
      }
    }
  }
}
